import type { TFunction } from 'i18next';
import { getApiErrorStatus } from '@/shared/api';

/** Наименьшая допустимая длина пароля в символах. */
export const PASSWORD_MIN_LENGTH = 8;

/** Наибольшая допустимая длина пароля в символах. */
const PASSWORD_MAX_LENGTH = 64;

/**
 * Спецсимволы, которые засчитывает правило надёжности пароля. Прочие символы в пароле допустимы,
 * но спецсимволом не считаются. Состав совпадает с проверкой сервера символ в символ —
 * расхождение пропустит пароль, который сервер отклонит, или отклонит тот, что сервер примет.
 */
export const PASSWORD_SPECIAL_CHARACTERS = '~!?@#$%^&*_-+()[]{}></\\|"\'.,:';

/**
 * Правила надёжности пароля — коды отказа сервера `422`, в том порядке, в каком сервер их перечисляет
 * и в каком они показываются пользователю.
 */
const PASSWORD_RULES = [
  'minLength',
  'maxLength',
  'latinLettersOnly',
  'uppercase',
  'lowercase',
  'digit',
  'specialCharacter',
  'personalEmail',
  'personalFirstName',
  'personalLastName',
  'personalMiddleName',
  'commonPassword',
  'sameAsCurrent',
] as const;

/** Правило надёжности пароля. */
export type PasswordRule = (typeof PASSWORD_RULES)[number];

/**
 * Что известно форме о владельце учётной записи, которой задаётся пароль. Правила о личных данных
 * проверяются по тем полям, что переданы; правило о совпадении с текущим паролем — только когда
 * форма его знает. Неизвестное форме проверяет сервер.
 */
export type PasswordRulesContext = {
  /** Email владельца */
  email?: string | null;
  /** Имя владельца */
  firstName?: string | null;
  /** Фамилия владельца */
  lastName?: string | null;
  /** Отчество владельца */
  middleName?: string | null;
  /** Текущий пароль учётной записи — его знает только смена пароля в профиле */
  currentPassword?: string | null;
};

// Слово из email или ФИО короче этого — слишком частый фрагмент, чтобы его запрещать
const MIN_PERSONAL_WORD_LENGTH = 3;

// Слово — подряд идущие буквы и цифры любого алфавита, как у сервера
const WORD_PATTERN = /[\p{L}\p{N}]+/gu;

// Кириллица латиницей, как имена пишутся в паспортах (ICAO Doc 9303): пароль с «Ivanov»
// содержит фамилию «Иванов» так же, как и её запись кириллицей. Таблица совпадает с серверной
const ICAO_TRANSLITERATION: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'e',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'i',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'kh',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'shch',
  ъ: 'ie',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'iu',
  я: 'ia',
  ґ: 'g',
  є: 'ie',
  і: 'i',
  ї: 'i',
  ў: 'u',
};

const SPECIAL_CHARACTERS = new Set(PASSWORD_SPECIAL_CHARACTERS);

// Длина — в символах, а не в кодовых единицах строки: символ вне базовой плоскости считается одним
const getLength = (value: string) => Array.from(value).length;

const getWords = (value: string) =>
  (value.toLowerCase().match(WORD_PATTERN) ?? []).filter((word) => getLength(word) >= MIN_PERSONAL_WORD_LENGTH);

const transliterate = (word: string) => Array.from(word, (letter) => ICAO_TRANSLITERATION[letter] ?? letter).join('');

/** Email владельца целиком и каждое достаточно длинное слово его локальной части, в нижнем регистре. */
const getEmailWords = (email: string | null | undefined): string[] => {
  const ownerEmail = email?.trim().toLowerCase();
  return ownerEmail ? [ownerEmail, ...getWords(ownerEmail.split('@')[0])] : [];
};

/**
 * Каждое достаточно длинное слово имени, фамилии или отчества в нижнем регистре — и в записи ФИО,
 * и в латинской. Достаточно ли слово длинное — решает запись в ФИО.
 */
const getNameWords = (name: string | null | undefined): string[] =>
  getWords(name ?? '').flatMap((word) => [word, transliterate(word)]);

const containsAny = (password: string, words: string[]) => {
  const lowered = password.toLowerCase();
  return words.some((word) => lowered.includes(word));
};

// Проверки, которые форма способна выполнить сама. Распространённые пароли проверяет только сервер:
// их перечень поставляется с бэком
const PASSWORD_RULE_CHECKS: Record<
  Exclude<PasswordRule, 'commonPassword'>,
  (password: string, context: PasswordRulesContext) => boolean
> = {
  minLength: (password) => getLength(password) >= PASSWORD_MIN_LENGTH,
  maxLength: (password) => getLength(password) <= PASSWORD_MAX_LENGTH,
  latinLettersOnly: (password) => !/(?![a-zA-Z])\p{L}/u.test(password),
  uppercase: (password) => /[A-Z]/.test(password),
  lowercase: (password) => /[a-z]/.test(password),
  digit: (password) => /[0-9]/.test(password),
  specialCharacter: (password) => Array.from(password).some((char) => SPECIAL_CHARACTERS.has(char)),
  personalEmail: (password, { email }) => !containsAny(password, getEmailWords(email)),
  personalFirstName: (password, { firstName }) => !containsAny(password, getNameWords(firstName)),
  personalLastName: (password, { lastName }) => !containsAny(password, getNameWords(lastName)),
  personalMiddleName: (password, { middleName }) => !containsAny(password, getNameWords(middleName)),
  sameAsCurrent: (password, { currentPassword }) => !currentPassword || password !== currentPassword,
};

const isCheckedByClient = (rule: PasswordRule): rule is keyof typeof PASSWORD_RULE_CHECKS =>
  rule in PASSWORD_RULE_CHECKS;

/** Правила надёжности, которые пароль не выполняет, из тех, что форма проверяет сама, — в порядке {@link PASSWORD_RULES}. */
export const getUnmetPasswordRules = (password: string, context: PasswordRulesContext): PasswordRule[] =>
  PASSWORD_RULES.filter((rule) => isCheckedByClient(rule) && !PASSWORD_RULE_CHECKS[rule](password, context));

/** Пароль длиннее допустимого — об этом сообщается сразу при вводе, не дожидаясь сохранения. */
export const isPasswordTooLong = (password: string) => !PASSWORD_RULE_CHECKS.maxLength(password, {});

// Недостающие символы отказ называет одной причиной «добавьте …»
const MISSING_CHARACTER_RULES = ['uppercase', 'lowercase', 'digit', 'specialCharacter'] as const;

// Найденные в пароле личные данные отказ называет одной причиной «в нём содержится …»
const PERSONAL_DATA_RULES = ['personalEmail', 'personalFirstName', 'personalLastName', 'personalMiddleName'] as const;

/** Слова перечислением на языке интерфейса: «a», «a и b», «a, b и c». */
const listWords = (words: string[], t: TFunction<'common'>) =>
  words.length > 1
    ? t('passwordRules.refusal.list', { rest: words.slice(0, -1).join(', '), last: words.at(-1) })
    : words.join('');

/**
 * Текст отказа по невыполненным правилам — на языке интерфейса, собран так же, как сервер собирает свой.
 * Все причины, кроме превышения длины, — одним предложением «Пароль ненадёжный, …» в порядке
 * {@link PASSWORD_RULES}; превышение длины — отдельным предложением перед ним.
 */
export const formatPasswordRefusal = (rules: PasswordRule[], t: TFunction<'common'>): string => {
  const missing = MISSING_CHARACTER_RULES.filter((rule) => rules.includes(rule)).map((rule) =>
    t(`passwordRules.refusal.${rule}`),
  );
  const found = PERSONAL_DATA_RULES.filter((rule) => rules.includes(rule)).map((rule) =>
    t(`passwordRules.refusal.${rule}`),
  );
  const reasons: string[] = [];
  if (rules.includes('minLength')) {
    reasons.push(t('passwordRules.refusal.minLength', { length: PASSWORD_MIN_LENGTH }));
  }
  if (rules.includes('latinLettersOnly')) {
    reasons.push(t('passwordRules.refusal.latinLettersOnly'));
  }
  if (missing.length > 0) {
    reasons.push(t('passwordRules.refusal.add', { characters: listWords(missing, t) }));
  }
  if (found.length > 0) {
    const data = listWords(found, t);
    reasons.push(
      found.length > 1
        ? t('passwordRules.refusal.containsMany', { data })
        : t('passwordRules.refusal.containsOne', { data }),
    );
  }
  if (rules.includes('commonPassword')) {
    reasons.push(t('passwordRules.refusal.commonPassword'));
  }
  if (rules.includes('sameAsCurrent')) {
    reasons.push(t('passwordRules.refusal.sameAsCurrent'));
  }

  const sentences: string[] = [];
  if (rules.includes('maxLength')) {
    sentences.push(t('passwordRules.refusal.maxLength', { length: PASSWORD_MAX_LENGTH }));
  }
  if (reasons.length > 0) {
    sentences.push(t('passwordRules.refusal.weak', { reasons: reasons.join('; ') }));
  }
  return sentences.join('. ');
};

const isPasswordRule = (code: unknown): code is PasswordRule =>
  typeof code === 'string' && (PASSWORD_RULES as readonly string[]).includes(code);

/**
 * Невыполненные правила из отказа сервера `422` по правилам надёжности (`detail.unmetRules`).
 * `null` — отказ не по правилам пароля, либо ни одно из названных сервером правил форме не известно:
 * тогда причину называет текст ответа.
 */
export const getPasswordRulesFromError = (error: unknown): PasswordRule[] | null => {
  if (getApiErrorStatus(error) !== 422) {
    return null;
  }
  const detail = (error as { data?: { detail?: unknown } }).data?.detail;
  if (typeof detail !== 'object' || detail === null || !('unmetRules' in detail) || !Array.isArray(detail.unmetRules)) {
    return null;
  }

  const codes: unknown[] = detail.unmetRules;
  const unknownCodes = codes.filter((code) => !isPasswordRule(code));
  if (unknownCodes.length > 0) {
    console.warn(`Правила пароля ${unknownCodes.join(', ')} форме не известны и у поля не показываются`);
  }
  const rules = PASSWORD_RULES.filter((rule) => codes.includes(rule));

  return rules.length > 0 ? rules : null;
};
